import React, { useState } from "react";
import {
  TextInput,
  Stack,
  Text,
  Divider,
  SimpleGrid,
  Paper,
  Group,
  Title,
  Box,
} from "@mantine/core";
import { IconReceipt2, IconBuildingBank } from "@tabler/icons-react";

export default function PaymentDetailsStep({ formData, setFormData }) {
  // Local state to manage the visual toggle. Defaults to gateway tracking.
  const [payoutMethod, setPayoutMethod] = useState("gateway");

  const updatePayment = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      paymentDetails: { ...prev.paymentDetails, [field]: value },
    }));
  };

  return (
    <Stack gap="xl">
      <Box>
        <Title order={3} size="h2" mb="xs">
          How should we send your money?
        </Title>
        <Text size="lg" c="dimmed">
          Choose your preferred method for receiving payouts from your sales.
          You can update these details later.
        </Text>
      </Box>

      {/* Sleek, Flat Card Selectors */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Paper
          withBorder
          p="xl"
          radius="md"
          onClick={() => setPayoutMethod("gateway")}
          style={{
            cursor: "pointer",
            transition: "all 0.2s ease",
            borderColor:
              payoutMethod === "gateway"
                ? "var(--mantine-color-blue-6)"
                : "var(--mantine-color-gray-3)",
            backgroundColor:
              payoutMethod === "gateway"
                ? "var(--mantine-color-blue-0)"
                : "white",
          }}
        >
          <Group gap="md" align="center">
            <IconReceipt2
              size={32}
              color={
                payoutMethod === "gateway"
                  ? "var(--mantine-color-blue-6)"
                  : "gray"
              }
            />
            <Box>
              <Text size="lg" fw={700}>
                Merchant Gateway Route
              </Text>
              <Text size="sm" c="dimmed">
                Best for automated client tracking (Razorpay)
              </Text>
            </Box>
          </Group>
        </Paper>

        <Paper
          withBorder
          p="xl"
          radius="md"
          onClick={() => setPayoutMethod("manual")}
          style={{
            cursor: "pointer",
            transition: "all 0.2s ease",
            borderColor:
              payoutMethod === "manual"
                ? "var(--mantine-color-blue-6)"
                : "var(--mantine-color-gray-3)",
            backgroundColor:
              payoutMethod === "manual"
                ? "var(--mantine-color-blue-0)"
                : "white",
          }}
        >
          <Group gap="md" align="center">
            <IconBuildingBank
              size={32}
              color={
                payoutMethod === "manual"
                  ? "var(--mantine-color-blue-6)"
                  : "gray"
              }
            />
            <Box>
              <Text size="lg" fw={700}>
                Manual Bank / UPI
              </Text>
              <Text size="sm" c="dimmed">
                Receive standard direct transfers to your account
              </Text>
            </Box>
          </Group>
        </Paper>
      </SimpleGrid>

      {/* Dynamic Inputs based on the selected card */}
      <Paper
        withBorder
        p="xl"
        radius="md"
        bg="gray.0"
        style={{ borderColor: "var(--mantine-color-gray-3)" }}
      >
        {payoutMethod === "gateway" ? (
          <Stack gap="md">
            <Text size="md" fw={600}>
              Gateway Details
            </Text>
            <TextInput
              label="Razorpay Account ID"
              size="lg"
              placeholder="Example: acc_Jk8xQwerty"
              description="Enter your merchant ID here to enable zero-fee, automated split-payment routing."
              required
              value={formData.paymentDetails.razorpayAccountId}
              onChange={(e) =>
                updatePayment("razorpayAccountId", e.target.value)
              }
            />
          </Stack>
        ) : (
          <Stack gap="lg">
            <Text size="md" fw={600}>
              Bank Transfer Details
            </Text>
            <TextInput
              label="UPI ID"
              size="lg"
              placeholder="Example: yourbrand@okicici"
              description="Type the UPI ID where you want your money sent."
              value={formData.paymentDetails.upiId}
              onChange={(e) => updatePayment("upiId", e.target.value)}
            />

            <Divider label="OR" labelPosition="center" />

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput
                label="Bank Account Number"
                size="lg"
                placeholder="Example: 123456789012"
                value={formData.paymentDetails.accountNumber}
                onChange={(e) => updatePayment("accountNumber", e.target.value)}
              />
              <TextInput
                label="IFSC Code"
                size="lg"
                placeholder="Example: HDFC0001234"
                value={formData.paymentDetails.ifscCode}
                onChange={(e) => updatePayment("ifscCode", e.target.value)}
              />
            </SimpleGrid>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
